import { appConfig } from "@/libs/appConfig";
import useGAnalytics, {
  EventAction,
  EventCategory,
  EventLabel,
} from "@/libs/hooks/tracking/useGAnalytics";
import useVisibilityTracker from "@/libs/hooks/tracking/useVisibilityTracking";
import { useIngredients } from "@/libs/hooks/useIngredients";
import { useSettings } from "@/libs/hooks/useSettings";
import { useMainDict } from "@/libs/hooks/useTranslation";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { Accordion } from "flowbite-react";
import Image from "next/image";
import { IconChevronDown } from "@tabler/icons-react";

export const IngredientsSection = () => {
  const { settings } = useSettings();
  const { ingredients } = useIngredients();

  const { trackEvent } = useGAnalytics();
  const { t } = useMainDict();

  useVisibilityTracker("ingredients-section", () => {
    trackEvent(
      "ingredients-section-visible",
      EventCategory.USER_INTERACTION,
      EventAction.BECOMES_VISIBLE,
      EventLabel.INGREDIENTS
    );
  });

  if (!settings?.showIngredients) {
    return null;
  }

  return (
    <section
      id="ingredients-section"
      aria-labelledby="ingredients-heading"
      className="relative w-full overflow-hidden"
    >
      {/* Vintage cream/parchment background */}
      <div className="absolute inset-0 bg-gradient-to-b from-vintage-cream via-vintage-parchment to-vintage-cream">
        {/* Subtle paper texture */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(44,24,16,0.08)_100%)]" />
      </div>

      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-ochre/40 to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-20">
        {/* Section header with vintage typography */}
        <div className="text-center mb-16">
          {/* Decorative element */}
          <div className="flex items-center justify-center gap-6 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rotate-45 border border-vintage-ochre/50" />
              <div className="w-20 h-px bg-vintage-ochre/50" />
            </div>
            <span className="text-vintage-terracotta text-2xl">✦</span>
            <div className="flex items-center gap-2">
              <div className="w-20 h-px bg-vintage-ochre/50" />
              <div className="w-3 h-3 rotate-45 border border-vintage-ochre/50" />
            </div>
          </div>

          <h2
            id="ingredients-heading"
            className="font-display text-5xl md:text-6xl font-bold text-vintage-burgundy tracking-wide uppercase"
          >
            {t("ingredientsHeader")}
          </h2>

          {/* Subtitle */}
          <p className="font-script text-vintage-terracotta text-2xl mt-4 transform -rotate-1">
            La Qualità è Tutto
          </p>

          {/* Decorative element */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <div className="w-12 h-px bg-vintage-ochre/50" />
            <span className="text-vintage-ochre text-lg">◆</span>
            <div className="w-12 h-px bg-vintage-ochre/50" />
          </div>
        </div>

        {/* Ingredients cards */}
        <div className="space-y-16">
          {ingredients?.map((item, index) => (
            <div
              key={item.id}
              className={`flex flex-col ${
                index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              } items-center gap-8 md:gap-12`}
            >
              {/* Image with vintage frame */}
              <div className="relative flex-shrink-0">
                {/* Decorative frame */}
                <div className="absolute -inset-4 border-2 border-vintage-ochre/30 rounded-sm" />
                <div className="absolute -inset-6 border border-vintage-ochre/20 rounded-sm hidden md:block" />

                <Image
                  unoptimized={true}
                  alt={item.attributes.name || "Ingredient image"}
                  className="rounded-sm shadow-vintage relative z-10 w-64 h-64 md:w-80 md:h-80 object-cover"
                  src={appConfig.assetsUrl + item.attributes.imageUrl}
                  width={320}
                  height={320}
                />

                {/* Corner ornaments */}
                <div className="absolute -top-2 -left-2 w-6 h-6 border-l-2 border-t-2 border-vintage-terracotta z-20" />
                <div className="absolute -top-2 -right-2 w-6 h-6 border-r-2 border-t-2 border-vintage-terracotta z-20" />
                <div className="absolute -bottom-2 -left-2 w-6 h-6 border-l-2 border-b-2 border-vintage-terracotta z-20" />
                <div className="absolute -bottom-2 -right-2 w-6 h-6 border-r-2 border-b-2 border-vintage-terracotta z-20" />
              </div>

              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-display text-3xl md:text-4xl font-bold text-vintage-espresso mb-4">
                  {item.attributes.name}
                </h3>

                {/* Decorative line */}
                <div className="flex items-center gap-3 mb-4 justify-center md:justify-start">
                  <div className="w-12 h-px bg-vintage-ochre" />
                  <div className="w-2 h-2 bg-vintage-ochre rotate-45" />
                </div>

                <div className="font-serif text-vintage-walnut text-lg leading-relaxed">
                  <BlocksRenderer
                    content={item.attributes.description}
                    blocks={{
                      paragraph: ({ children }) => (
                        <p className="mb-4">{children}</p>
                      ),
                    }}
                  />
                </div>

                {/* Read more accordion */}
                {item.attributes.readMoreTitle && (
                  <div className="mt-6 vintage-accordion">
                    <Accordion
                      className="border-none bg-transparent"
                      collapseAll
                    >
                      <Accordion.Panel>
                        <Accordion.Title className="text-vintage-terracotta bg-transparent hover:bg-vintage-terracotta/5 focus:ring-0 font-condensed tracking-wider uppercase text-sm px-0 py-3 border-b border-vintage-ochre/30">
                          {item.attributes.readMoreTitle}
                        </Accordion.Title>
                        <Accordion.Content className="bg-transparent px-0 py-4">
                          <div className="font-serif text-vintage-walnut leading-relaxed">
                            <BlocksRenderer
                              content={item.attributes.readMoreDescription}
                              blocks={{
                                paragraph: ({ children }) => (
                                  <p className="mb-3">{children}</p>
                                ),
                              }}
                            />
                          </div>
                        </Accordion.Content>
                      </Accordion.Panel>
                    </Accordion>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom decorative border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-ochre/40 to-transparent" />
    </section>
  );
};
